// src/state/notifications/NotificationContext.js
import React, { Component, createContext } from 'react';
import { urlMap } from '../../utils';

export const NotificationContext = createContext(null);

const makeId = () =>
  `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const WS_URL = urlMap.notificationsWs();
const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export class NotificationProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      connected: false,
      wsEnabled: false, // <--- toggle this to disable WS entirely
    };

    this.add = this.add.bind(this);
    this.remove = this.remove.bind(this);
    this.clear = this.clear.bind(this);
    this.markRead = this.markRead.bind(this);
    this.markAllRead = this.markAllRead.bind(this);

    this._walletRefreshCb = null;
    this.registerWalletRefresh = this.registerWalletRefresh.bind(this);
    this.notifyWalletRefresh  = this.notifyWalletRefresh.bind(this);

    this.ws = null;
    this.backoff = 0;
    this.heartbeatTimer = null;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  componentDidMount() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.state.wsEnabled) {
      this.connect(true);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.teardown('unmount');
  }

  handleVisibilityChange() {
    if (!this.state.wsEnabled) return;
    if (document.hidden) {
      this.teardown('hidden');
    } else if (!this.state.connected) {
      this.connect(true);
    }
  }

  add(titleOrObj, time = 'just now') {
    const item =
      typeof titleOrObj === 'string'
        ? { id: makeId(), title: titleOrObj, time, read: false }
        : { read: false, time: 'just now', ...titleOrObj, id: titleOrObj.id || makeId() };

    this.setState((prev) => ({ items: [item, ...prev.items] }));
    return item.id;
  }

  remove(id) {
    this.setState((prev) => ({ items: prev.items.filter((i) => i.id !== id) }));
    this.send({ type: 'notification:remove', id });
  }

  clear() {
    const ids = this.state.items.map((i) => i.id);
    this.setState({ items: [] });
    this.send({ type: 'notification:clear', ids });
  }

  markRead(id) {
    this.setState((prev) => ({
      items: prev.items.map((i) => (i.id === id ? { ...i, read: true } : i)),
    }));
    this.send({ type: 'notification:mark_read', id });
  }

  markAllRead() {
    const unreadIds = this.state.items.filter((i) => !i.read).map((i) => i.id);
    if (unreadIds.length === 0) return;
    this.setState((prev) => ({ items: prev.items.map((i) => ({ ...i, read: true })) }));
    this.send({ type: 'notification:mark_all_read', ids: unreadIds });
  }

  registerWalletRefresh(cb) {
    this._walletRefreshCb = cb;
    // return unregister fn
    return () => { if (this._walletRefreshCb === cb) this._walletRefreshCb = null; };
  }

  notifyWalletRefresh() {
    if (typeof this._walletRefreshCb === 'function') this._walletRefreshCb();
  }

  connect(resetBackoff = false) {
    if (!this.state.wsEnabled) return;
    try {
      if (resetBackoff) this.backoff = 0;
      if (this.ws) this.teardown('reconnect');

      const token = localStorage.getItem('token') || '';
      const url = `${WS_URL}?token=${encodeURIComponent(token)}`;

      const ws = new WebSocket(url);
      this.ws = ws;

      ws.onopen = () => {
        this.setState({ connected: true });
        this.backoff = 0;
        this.armHeartbeat();
        this.send({ type: 'notifications:sync' });
      };

      ws.onmessage = (evt) => {
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return;
        }
        switch (msg.type) {
          case 'pong':
            break;
          case 'notifications:snapshot':
            this.setState({
              items: (msg.items || []).map((n) => ({
                read: !!n.read,
                time: n.time || 'now',
                ...n,
              })),
            });
            break;
          case 'notification:new':
            if (msg.notification) this.add({ ...msg.notification });
            break;
          case 'notification:remove':
            if (msg.id) {
              this.setState((prev) => ({
                items: prev.items.filter((i) => i.id !== msg.id),
              }));
            }
            break;
          case 'notification:mark_read':
            if (msg.id) {
              this.setState((prev) => ({
                items: prev.items.map((i) =>
                  i.id === msg.id ? { ...i, read: true } : i
                ),
              }));
            }
            break;
          case 'notification:mark_all_read':
            this.setState((prev) => ({
              items: prev.items.map((i) => ({ ...i, read: true })),
            }));
            break;
          case 'toast':
            if (msg.title) this.add({ title: msg.title, time: msg.time || 'now' });
            break;
          default:
            break;
        }
      };

      ws.onerror = () => {};
      ws.onclose = () => {
        this.setState({ connected: false });
        this.disarmHeartbeat();
        this.scheduleReconnect();
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  teardown(reason) {
    this.disarmHeartbeat();
    if (this.ws) {
      try {
        this.ws.close(1000, reason);
      } catch {}
      this.ws = null;
    }
  }

  scheduleReconnect() {
    if (!this.state.wsEnabled) return;
    if (document.hidden) return;
    const base = Math.min(MAX_BACKOFF_MS, (this.backoff || 1000) * 2);
    const delay = Math.floor(base * (0.5 + Math.random()));
    this.backoff = base;
    setTimeout(() => this.connect(), delay);
  }

  send(obj) {
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(obj));
      }
    } catch {}
  }

  armHeartbeat() {
    this.disarmHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping', ts: Date.now() });
    }, HEARTBEAT_MS);
  }

  disarmHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  render() {
    const { items, connected, wsEnabled } = this.state;
    const value = {
      items,
      unreadCount: items.filter((i) => !i.read).length,
      connected: wsEnabled && connected,
      wsEnabled,
      add: this.add,
      remove: this.remove,
      clear: this.clear,
      markRead: this.markRead,
      markAllRead: this.markAllRead,
      registerWalletRefresh: this.registerWalletRefresh,
      notifyWalletRefresh:   this.notifyWalletRefresh,
      setWsEnabled: (enabled) => {
        this.setState({ wsEnabled: enabled }, () => {
          if (!enabled) this.teardown('disabled');
          else if (!this.state.connected) this.connect(true);
        });
      },
    };

    return (
      <NotificationContext.Provider value={value}>
        {this.props.children}
      </NotificationContext.Provider>
    );
  }
}

export function withNotifications(Wrapped) {
  return class WithNotifications extends Component {
    render() {
      return (
        <NotificationContext.Consumer>
          {(api) => <Wrapped {...this.props} notificationsApi={api} />}
        </NotificationContext.Consumer>
      );
    }
  };
}
